import React from 'react';
import { Card, Text } from '@shopify/polaris';

// Error Boundary class component as recommended in SHOPIFY-APP-APPROVAL-GUIDE.md
class ComponentErrorBoundary extends React.Component {
  constructor(props) {
    super(props);
    this.state = { hasError: false, error: null, errorInfo: null };
  }

  static getDerivedStateFromError(error) {
    return { hasError: true, error };
  }

  componentDidCatch(error, errorInfo) {
    console.error(`Error in ${this.props.componentName}:`, error, errorInfo);
    this.setState({ errorInfo });
  }

  render() {
    if (this.state.hasError) {
      return (
        <Card sectioned>
          <div style={{
            backgroundColor: '#fef2f2',
            border: '1px solid #ef4444',
            borderRadius: '6px',
            padding: '16px'
          }}>
            <div>
              <Text as="h3" variant="headingMd" style={{ color: '#dc2626' }}>Component Error: {this.props.componentName}</Text>
              <p style={{ color: '#dc2626' }}>This component failed to render properly. Please refresh the page or try accessing it from within Shopify Admin.</p>
              {process.env.NODE_ENV === 'development' && (
                <details style={{ marginTop: '10px' }}>
                  <summary style={{ color: '#dc2626' }}>Error Details (Development Only)</summary>
                  <pre style={{ fontSize: '12px', marginTop: '10px', whiteSpace: 'pre-wrap', color: '#dc2626' }}>
                    {this.state.error && this.state.error.toString()}
                    {this.state.errorInfo && this.state.errorInfo.componentStack}
                  </pre>
                </details>
              )}
            </div>
          </div>
        </Card>
      );
    }
    return this.props.children;
  }
}

// Wrapper function to create safe components
export const createSafeComponent = (Component, name) => {
  const SafeComponent = (props) => (
    <ComponentErrorBoundary componentName={name}>
      <Component {...props} />
    </ComponentErrorBoundary>
  );
  
  // Preserve component name for debugging
  SafeComponent.displayName = `Safe(${name})`;
  return SafeComponent;
};

// Main App Error Boundary for critical errors
export class AppErrorBoundary extends React.Component {
  constructor(props) {
    super(props);
    this.state = { hasError: false, error: null };
  }

  static getDerivedStateFromError(error) {
    return { hasError: true, error };
  }

  componentDidCatch(error, errorInfo) {
    console.error('Critical App Error:', error, errorInfo);
  }

  render() {
    if (this.state.hasError) {
      return (
        <div style={{ padding: '40px', textAlign: 'center' }}>
          <Card sectioned>
            <div style={{
              backgroundColor: '#fef2f2',
              border: '1px solid #ef4444',
              borderRadius: '6px',
              padding: '16px'
            }}>
              <div>
                <Text as="h2" variant="headingLg" style={{ color: '#dc2626' }}>Application Error</Text>
                <p style={{ color: '#dc2626' }}>TrackingMania encountered an unexpected error. This usually happens when:</p>
                <ul style={{ textAlign: 'left', marginTop: '15px', color: '#dc2626' }}>
                  <li>The app is accessed outside of Shopify Admin</li>
                  <li>The Shopify session has expired</li>
                  <li>There's a component compatibility issue</li>
                </ul>
                <p style={{ marginTop: '20px', color: '#dc2626' }}>
                  <strong>Solution:</strong> Please access TrackingMania through your Shopify Admin → Apps → TrackingMania
                </p>
              </div>
            </div>
          </Card>
        </div>
      );
    }
    return this.props.children;
  }
}

export default ComponentErrorBoundary;